import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Add, Edit, Delete, Theaters } from '@mui/icons-material';
import { API_BASE_URL } from '../utils/api';
import AnimControllerStudio from './animController/AnimControllerStudio';

const PACK_OPTIONS = [
  { value: 'Lina', label: 'Lina' },
  { value: 'Mortifera', label: 'Mortifera' }
];

export default function AnimControllersPanel({ onNotify }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metaOpen, setMetaOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formName, setFormName] = useState('');
  const [formPack, setFormPack] = useState('Lina');
  const [studioRow, setStudioRow] = useState(null);

  const show = useCallback(
    (message, severity = 'success') => {
      onNotify?.(message, severity);
    },
    [onNotify]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Load failed');
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      show(e.message || 'Error loading anim controllers', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormPack('Lina');
    setMetaOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormName(row.name || '');
    setFormPack(row.live2dPack || 'Lina');
    setMetaOpen(true);
  };

  const closeMeta = () => {
    setMetaOpen(false);
    setEditing(null);
  };

  const submitMeta = async () => {
    const name = formName.trim();
    if (!name) {
      show('Name is required', 'error');
      return;
    }
    try {
      if (editing) {
        const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers/${editing._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, live2dPack: formPack })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Update failed');
        show('Controller updated');
      } else {
        const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, live2dPack: formPack })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || 'Create failed');
        show('Controller created');
      }
      closeMeta();
      await fetchRows();
    } catch (e) {
      show(e.message || 'Request failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this anim controller?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/live2d-anim-controllers/${id}`, {
        method: 'DELETE'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || 'Delete failed');
      show('Controller deleted');
      if (studioRow?._id === id) setStudioRow(null);
      await fetchRows();
    } catch (e) {
      show(e.message || 'Delete failed', 'error');
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
          Animation controllers use <code>/live2d/&lt;pack&gt;/index.json</code> (Lina / Mortifera). Open Studio to build the
          graph: each node stores its own clip; runtime state uses the active node, not a separate default on the document.
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          New controller
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Pack (index.json)</TableCell>
                <TableCell>Graph</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 140 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No controllers yet. Create one and assign Lina or Mortifera.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="primary">
                        {row.live2dPack}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {row.indexUrl || `/live2d/${row.live2dPack}/index.json`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {Array.isArray(row.animGraph?.nodes) ? `${row.animGraph.nodes.length} nodes` : '—'}
                      </Typography>
                      {Array.isArray(row.animGraph?.edges) ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.animGraph.edges.length} links
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="secondary" onClick={() => setStudioRow(row)} title="Studio">
                        <Theaters fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => openEdit(row)} title="Edit">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(row._id)} title="Delete">
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={metaOpen} onClose={closeMeta} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit anim controller' : 'New anim controller'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              required
              placeholder="e.g. Lobby idle, Cutscene A"
            />
            <FormControl fullWidth>
              <InputLabel id="anim-pack-label">Live2D pack</InputLabel>
              <Select
                labelId="anim-pack-label"
                label="Live2D pack"
                value={formPack}
                onChange={(e) => setFormPack(e.target.value)}
              >
                {PACK_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label} → /live2d/{o.value}/index.json
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMeta}>Cancel</Button>
          <Button variant="contained" onClick={submitMeta}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {studioRow ? (
        <AnimControllerStudio
          key={studioRow._id}
          row={studioRow}
          onClose={() => setStudioRow(null)}
          onSaved={async (updated) => {
            await fetchRows();
            if (updated?._id) {
              setStudioRow((r) => (r && r._id === updated._id ? { ...r, ...updated } : r));
            }
          }}
          onNotify={show}
        />
      ) : null}
    </>
  );
}
